import { Autocomplete, Chip, IconButton, MenuItem, Paper, Select, Skeleton, SvgIcon, TextField, Typography } from "@mui/material";
import { Box, Stack } from "@mui/system";
import { DataGridPro, GridColDef, GridColumnVisibilityModel, GridPagination, GridRenderCellParams } from "@mui/x-data-grid-pro";
import { useCallback, useMemo } from "react";
import { useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router";
import CustomTooltip from "src/components/visibility/components/CustomTooltip";
import Iconify from "src/components/iconify";
import { useGetBrandsQuery, useGetIndustriesQuery } from "src/context/api/leaderboard/api";
import { useCurrentWorkspace } from "src/context/reducers/app-settings";
import { formatCurrency } from "src/utils/format-currency";
import { formatNumber, fPercent } from "src/utils/format-number";
import RegionSelector from "./region-selector";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "src/context/store";
import PresenceRateChart from "src/components/visibility/components/PresenceRateChart";
import DatagridDiffCell from "src/components/data-grid/data-grid-diff-cell";
import { t } from "i18next";
import CompareChip from "src/components/chip/compare-chip";
import ModalTrackingBrand from "./modalTrackingBrand";

export default function Leaderboard() {
  const navigate = useNavigate();
  const apiRef: any = useRef();
  const workspace: any = useCurrentWorkspace();
  const workspaceId: any = workspace?.id;
  const lang = localStorage.getItem('i18nextLng') || 'en';
  const filterStore = useSelector((state: RootState) => state.filters);

  const [modalTrackingBrand, setModalTrackingBrand]: any = useState<{
    open: boolean;
    domain: string | null;
  }>({ open: false, domain: null });

  const dateRangeOptions = [
    { label: 'Last Month', value: 'last_month' },
    { label: 'Last 3 Months', value: 'last_3_months' },
    { label: 'Last 6 Months', value: 'last_6_months' },
    { label: 'This Year', value: 'this_year' },
    { label: 'Last Year', value: 'last_year' },
  ];

  const comparisonPeriodOptions = [
    { label: 'No Comparison', value: 'no_comparison' },
    { label: 'Prev. Period', value: 'prev_period' },
    { label: 'Prev. Year', value: 'prev_year' },
  ];

  const [selectedDateRange, setSelectedDateRange] = useState(dateRangeOptions[1]);
  const [selectedComparisonPeriod, setSelectedComparisonPeriod]: any = useState(comparisonPeriodOptions[0]);
  const [selectedIndustries, setSelectedIndustries] = useState<string[]>([]);


  const [paginationModel, setPaginationModel] = useState({
    pageSize: 10,
    page: 0,
  });

  const { data: industriesData }: any = useGetIndustriesQuery(
    {
      workspace_id: workspaceId,
      country: filterStore?.countries[0]
    },
    {
      skip: !workspaceId || !filterStore?.countries[0],
    }
  );


  const toolbarRef: any = useRef();

  const CustomFooter = ({
    paginationModel,
    onPageSizeChange,
    rows
  }: any) => {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          backgroundColor: "#FAFAFA",
          px: 2,
          py: 0,
          gap: 2,
          borderBottomRightRadius: "8px",
          borderBottomLeftRadius: "8px",

        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} height={"100%"}>
          <GridPagination />
        </Stack>
      </Box>
    );
  };

  const CustomToolbar = (props: any) => {
    const industries = industriesData?.industries || [];


    return (
      <Stack
        ref={toolbarRef}
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={0}
        px={2.5}
        py={1.25}
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          backgroundColor: 'white',
          borderTopRightRadius: '8px',
          borderTopLeftRadius: '8px',
          borderBottom: '1px dashed rgba(145, 158, 171, 0.20)',
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" px={1} width="100%">
          {/* LEFT BUTTONS */}
          <Stack direction="row" spacing={1}>
            <IconButton
              onClick={() => apiRef.current?.showPreferences("columns")}
              sx={{
                width: 26,
                height: 26,
                padding: 0,
                color: '#919EAB',
                '&:hover': { backgroundColor: 'action.hover' },
              }}
            >
              <Iconify icon="mingcute:calendar-week-line" width={18} height={18} />
            </IconButton>
            <IconButton
              onClick={() => apiRef.current?.showFilterPanel()}
              sx={{
                width: 26,
                height: 26,
                padding: 0,
                color: '#919EAB',
                '&:hover': { backgroundColor: 'action.hover' },
              }}
            >
              <Iconify icon="ic:round-filter-list" width={18} height={18} />
            </IconButton>
            <IconButton
              onClick={() => apiRef.current?.exportDataAsCsv({
                fileName: 'leaderboard',
                utf8WithBom: true
              })}
              sx={{
                width: 24,
                height: 24,
                padding: 0,
                color: '#919EAB',
                '&:hover': {
                  backgroundColor: 'action.hover',
                }
              }}
            >
              <Iconify icon="lets-icons:export" width={16} height={16} />
            </IconButton>
          </Stack>

          {/* RIGHT FILTERS */}
          <Stack direction="row" spacing={1} alignItems="center">
            <Autocomplete
              multiple
              disablePortal
              options={industries}
              value={selectedIndustries}
              onChange={(event, newValue) => setSelectedIndustries(newValue)}
              getOptionLabel={(option) => option}
              size="small"
              sx={{ width: 300 }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        {selectedIndustries.length === 0 && (
                          <Chip
                            label="Industries"
                            size="small"
                            sx={{
                              backgroundColor: 'rgba(145, 158, 171, 0.32)',
                              color: 'rgba(51, 60, 71, 1)',
                              mr: 0.5,
                              ":hover": {
                                backgroundColor: 'rgba(145, 158, 171, 0.62)',
                              }
                            }}
                          />
                        )}

                        {selectedIndustries.length === 1 && (
                          <Chip
                            label={selectedIndustries[0]}
                            size="small"
                            sx={{
                              backgroundColor: 'rgba(145, 158, 171, 0.32)',
                              color: 'rgba(51, 60, 71, 1)',
                              mr: 0.5,
                              ":hover": {
                                backgroundColor: 'rgba(145, 158, 171, 0.62)',
                              }
                            }}
                          />
                        )}

                        {selectedIndustries.length > 1 && (
                          <>
                            <Chip
                              label={selectedIndustries[0]}
                              size="small"
                              sx={{
                                backgroundColor: 'rgba(145, 158, 171, 0.32)',
                                color: 'rgba(51, 60, 71, 1)',
                                mr: 0.5,
                                ":hover": {
                                  backgroundColor: 'rgba(145, 158, 171, 0.62)',
                                }
                              }}
                            />
                            <Chip
                              label={`+${selectedIndustries.length - 1}`}
                              size="small"
                              sx={{
                                backgroundColor: 'rgba(145, 158, 171, 0.32)',
                                color: 'rgba(51, 60, 71, 1)',
                                mr: 0.5,
                                ":hover": {
                                  backgroundColor: 'rgba(145, 158, 171, 0.62)',
                                }
                              }}
                            />
                          </>
                        )}

                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />

            <Autocomplete
              disablePortal
              options={dateRangeOptions}
              size="small"
              disableClearable
              onChange={(event, newValue) => setSelectedDateRange(newValue!)}
              getOptionLabel={(option) => option.label}
              sx={{ width: 150 }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        {selectedDateRange && (
                          <Chip
                            label={selectedDateRange.label}
                            size="small"
                            sx={{
                              backgroundColor: 'rgba(145, 158, 171, 0.32)',
                              color: 'rgba(51, 60, 71, 1)',
                              mr: 1,
                              "&:hover": {
                                backgroundColor: "rgba(145, 158, 171, 0.5)"
                              }
                            }}
                          />
                        )}
                      </>
                    ),
                  }}
                />
              )}
            />
            <Autocomplete
              disablePortal
              options={comparisonPeriodOptions}
              disableClearable
              size="small"
              onChange={(event, newValue) => setSelectedComparisonPeriod(newValue!)}
              getOptionLabel={(option) => option.label}
              sx={{ width: 160 }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        {selectedComparisonPeriod && (
                          <Chip
                            label={selectedComparisonPeriod.label}
                            size="small"
                            sx={{
                              backgroundColor: 'rgba(145, 158, 171, 0.32)',
                              color: 'rgba(51, 60, 71, 1)',
                              mr: 1,
                              "&:hover": {
                                backgroundColor: "rgba(145, 158, 171, 0.5)"
                              }
                            }}
                          />
                        )}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Stack>
        </Stack>
      </Stack>
    )
  }

  const columns: GridColDef[] = [
    {
      field: 'domains',
      headerAlign: "left",
      flex: 1,
      headerName: t('dashboard.global.brandPulse.domains'),
      minWidth: 350,
      maxWidth: 450,
      disableColumnMenu: true,
      renderHeader: () => (
        <Box>
          <Typography color={"#637381"} fontSize={"14px"} fontWeight={600}>{t('dashboard.global.brandPulse.domains')}</Typography>
        </Box>
      ),
      renderCell: (params: any) => (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 1, height: 1, width: "100%" }}
        >
          {/* LEFT SIDE */}
          <Box display="flex" alignItems="center" gap={1} minWidth={0}>

            {/* FAVICON */}
            <Box
              sx={{
                width: 24,
                height: 24,
                border: "1px solid rgba(129, 129, 129, 0.2)",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <img
                src={`https://www.google.com/s2/favicons?domain=${params.row.domains}&sz=32`}
                alt="favicon"
                style={{ width: 16, height: 16 }}
              />
            </Box>

            {/* DOMAIN (TRUNCATED) */}
            <Typography
              fontSize="14px"
              color="rgba(47, 56, 67, 1)"
              noWrap
              sx={{
                maxWidth: 120, // ajuste selon ton UI
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                cursor: params.row.data_status ? "pointer" : "default",
              }}
            >
              {params.row.domains}
            </Typography>
          </Box>

          {/* RIGHT SIDE */}
          <Box>
            <Typography
              variant="caption"
              sx={{
                background:
                  "linear-gradient(90deg, rgba(249, 249, 250, 0.00) 0%, #F9F9FA 55.09%)",
                color: "rgba(0, 108, 156, 0.85)",
                padding: "2px 4px",
                borderRadius: "4px",
                display: "flex",
                alignItems: "center",
                gap: "4px",
                cursor: "pointer",
                whiteSpace: "nowrap",
                "&:hover": {
                  textDecoration: "underline"
                }
              }}
              onClick={() =>
                params.row.data_status &&
                setModalTrackingBrand({
                  open: true,
                  domain: params?.row?.domains,
                })
              }
            >
              See evolution
              <SvgIcon
                component={() => (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="8"
                    height="8"
                    viewBox="0 0 8 8"
                    fill="none"
                  >
                    <path
                      d="M0.599609 7.26628L7.26628 0.599609M7.26628 0.599609H0.599609M7.26628 0.599609V7.26628"
                      stroke="#006C9C"
                      strokeOpacity="0.85"
                      strokeWidth="1.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              />
            </Typography>
          </Box>
        </Stack>
      ),
    },
    {
      field: 'industry',
      headerName: "Industry",
      flex: 1,
      maxWidth: 180,
      renderCell: (params: any) => (
        <Box
          sx={{
            borderRadius: 1,
            backgroundColor: "rgba(171, 204, 237, 0.2)",
            padding: "2px 6px",
            color: "#17407F",
            fontVariantNumeric: "oldstyle-nums tabular-nums",
            fontFamily: 'var(--font-family-Font-1, "Public Sans")',
            fontSize: 13,
            fontWeight: 400,
            lineHeight: "22px",

            display: "inline-block",
            whiteSpace: "normal",
            wordBreak: "break-word",
            "&:hover": {
              textDecoration: "underline dotted",
            },
            cursor: 'pointer',
            textAlign: "center",
          }}
          onClick={() => {
            setSelectedIndustries((prev: string[]) => {
              const industry = params.row.industry;
              if (prev.includes(industry)) {
                return prev.filter((i) => i !== industry);
              }
              return [...prev, industry];
            });
          }}
        >
          {params.row.industry}
        </Box>
      )
    },
    {
      field: 'monthly_searches',
      disableColumnMenu: true,
      flex: 1,
      headerAlign: "right",
      headerName: t('dashboard.global.brandPulse.monthly_searches'),
      renderHeader: () => (
        <CustomTooltip
          title={t('dashboard.global.brandPulse.definitions.monthly_searches')}
          placement="top"
        >
          <Box
            sx={{
              cursor: "help",
            }}
          >
            <Typography
              sx={{
                whiteSpace: "normal",
                wordBreak: "break-word",
                lineHeight: 1.2,
              }}
              color="#637381"
              fontSize="14px"
              fontWeight={600}
            >
              {t('dashboard.global.brandPulse.monthly_searches')}
            </Typography>
          </Box>
        </CustomTooltip>
      ),
      type: 'number',
      align: 'right',
      renderCell: (params: any) => (
        <>
          {params.row.data_status &&
            <Stack spacing={1} direction="row" alignItems="center" justifyContent={"flex-end"} sx={{ px: 1, width: 1, height: 1 }}>
              <Typography fontSize={"14px"}>
                {formatNumber(params.row.monthly_searches)}
              </Typography>
            </Stack>
          }
        </>
      ),
    },
    selectedComparisonPeriod?.value !== 'no_comparison' ?
      {
        field: 'monthly_search_volume_compare',
        disableColumnMenu: true,
        flex: 1,
        headerAlign: "right",
        headerName: t('dashboard.global.brandPulse.monthly_searches_compare'),
        renderHeader: () => (
          <CustomTooltip
            title={t('dashboard.global.brandPulse.definitions.monthly_searches')}
            placement="top"
          >
            <Box
              sx={{
                cursor: "help",
              }}
            >
              <Typography
                sx={{
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                  lineHeight: 1.2,
                }}
                color="#637381"
                fontSize="14px"
                fontWeight={600}
              >
                {t('dashboard.global.brandPulse.monthly_searches_compare')}
              </Typography>
            </Box>
          </CustomTooltip>
        ),
        type: 'number',
        align: 'right',
        renderCell: (params: any) => (
          <>
            {params.row.data_status &&
              <Stack spacing={1} direction="row" alignItems="center" justifyContent={"flex-end"} sx={{ px: 1, width: 1, height: 1 }}>
                <Typography fontSize={"14px"} sx={{ opacity: .5 }}>
                  {formatNumber(params.row.monthly_search_volume_compare)}
                </Typography>
              </Stack>
            }
          </>
        ),
      } : null,
    selectedComparisonPeriod?.value !== 'no_comparison' ?
      {
        field: 'monthly_search_volume_difference',
        headerAlign: "right",
        disableColumnMenu: true,
        flex: 1,
        headerName: t('dashboard.global.brandPulse.monthly_searches_difference'),
        renderHeader: () => (
          <CustomTooltip
            title={t('dashboard.global.brandPulse.definitions.monthly_searches')}
            placement="top"
          >
            <Box
              sx={{
                cursor: "help",
              }}
            >
              <Typography
                sx={{
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                  lineHeight: 1.2,
                }}
                color="#637381"
                fontSize="14px"
                fontWeight={600}
              >
                {t('dashboard.global.brandPulse.monthly_searches_difference')}
              </Typography>
            </Box>
          </CustomTooltip>
        ),
        type: 'number',
        align: 'right',
        renderCell: (params: any) => {
          const current = params.row.monthly_searches || 0;
          const compare = params.row?.monthly_search_volume_compare || 0;

          const diffValue = current - compare;
          const diffPercent = compare !== 0 ? (diffValue / compare) * 100 : 0;

          return (
            <>
              {
                params.row.data_status &&

                <DatagridDiffCell
                  value={formatNumber(diffValue)}
                  compareChip={diffPercent}
                  colors={{
                    dark: '#db8377',
                    light: '#B71D18'
                  }}
                />
              }
            </>
          );
        },
      } : null,
    {
      field: 'organic_vs_paid',
      disableColumnMenu: true,
      headerAlign: "right",
      flex: 1,
      filterable: false,
      headerName: t('dashboard.global.brandPulse.organic_vs_paid'),
      renderHeader: () => (
        <CustomTooltip
          title={t('dashboard.global.brandPulse.definitions.organic_vs_paid')}
          placement="top"
        >
          <Box
            sx={{
              cursor: "help",
            }}
          >
            <Typography
              sx={{
                whiteSpace: "normal",
                wordBreak: "break-word",
                lineHeight: 1.2,
              }}
              color="#637381"
              fontSize="14px"
              fontWeight={600}
            >
              {t('dashboard.global.brandPulse.organic_vs_paid')}
            </Typography>
          </Box>
        </CustomTooltip>
      ),
      align: 'center',
      renderCell: (params: any) => {
        const tooltipContent = (
          <Box sx={{ width: '250px' }}>
            <Box className="visibility-chart-tooltip-header" sx={{ mb: 1, fontWeight: 'bold' }}>
              <Typography className="visibility-chart-tooltip-title" variant="body2">
                {t('dashboard.global.brandPulse.organic_paid')}
              </Typography>
            </Box>

            <Box className="visibility-chart-tooltip-row" sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <div className="visibility-chart-tooltip-indicator" style={{ backgroundColor: '#18A155' }}></div>

              <Typography className="visibility-chart-tooltip-label" variant="body2">
                {t('dashboard.global.brandPulse.organic')}
              </Typography>
              <Typography sx={{ color: 'rgba(51, 60, 71, 1)' }} variant="body2">
                {formatNumber(params?.row?.organic_volume?.toFixed(0))}
              </Typography>
            </Box>

            <Box className="visibility-chart-tooltip-row" sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <div className="visibility-chart-tooltip-indicator" style={{ backgroundColor: '#C09910' }}></div>

              <Typography className="visibility-chart-tooltip-label" variant="body2">
                {t('dashboard.global.brandPulse.paid')}
              </Typography>
              <Typography sx={{ color: 'rgba(51, 60, 71, 1)' }} variant="body2">
                {formatNumber(params?.row?.paid_volume?.toFixed(0))}
              </Typography>
            </Box>
          </Box>
        );

        return (
          <>
            {params.row.data_status &&
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ width: '100%', height: "100%" }}
              >
                <Typography color={"rgba(47, 56, 67, 1)"} variant="caption" sx={{ minWidth: 30, textAlign: 'right' }} fontSize={'0.875rem'}>
                  {params?.row?.organic?.toFixed(0)}%
                </Typography>

                <CustomTooltip noPadding title={tooltipContent} placement="top">

                  <Box
                    sx={{
                      position: 'relative',
                      flexGrow: 1,
                      height: 8,
                      border: "none",
                      borderRadius: 1,
                      backgroundColor: 'transparent',
                      display: 'flex',
                    }}
                  >
                    <Box
                      sx={{
                        width: `${params?.row?.organic?.toFixed(0)}%`,
                        backgroundColor: '#18A155',
                        borderRadius: "2px",
                      }}
                    />

                    <Box
                      sx={{
                        width: `${params?.row?.paid?.toFixed(0)}%`,
                        backgroundColor: '#DAA335',
                        borderRadius: "2px",
                        ml: '2px',
                      }}
                    />
                  </Box>
                </CustomTooltip>

                <Typography color={"rgba(47, 56, 67, 1)"} variant="caption" sx={{ minWidth: 30, textAlign: 'left' }} fontSize={'0.875rem'}>
                  {params?.row?.paid?.toFixed(0)}%
                </Typography>
              </Stack>
            }
          </>
        );
      }
    },
    selectedComparisonPeriod?.value !== 'no_comparison' ?
      {
        field: 'organic_vs_paid_compare',
        disableColumnMenu: true,
        headerAlign: "right",
        flex: 1,
        filterable: false,
        headerName: t('dashboard.global.brandPulse.organic_vs_paid_compare'),
        renderHeader: () => (
          <CustomTooltip
            title={t('dashboard.global.brandPulse.definitions.organic_vs_paid')}
            placement="top"
          >
            <Box
              sx={{
                cursor: "help",
              }}
            >
              <Typography
                sx={{
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                  lineHeight: 1.2,
                }}
                color="#637381"
                fontSize="14px"
                fontWeight={600}
              >
                {t('dashboard.global.brandPulse.organic_vs_paid_compare')}
              </Typography>
            </Box>
          </CustomTooltip>
        ),
        align: 'center',
        renderCell: (params: any) => {
          const tooltipContent = (
            <Box sx={{ minWidth: 280, padding: 0, margin: 0 }}>
              <Stack direction="column">
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ padding: '13px', backgroundColor: 'rgba(226, 229, 233, 0.16)' }}>
                  <Typography fontSize={'14px'} color={"rgba(99, 115, 129, 1)"}>{t('dashboard.global.brandPulse.organic_paid')}</Typography>
                </Stack>
                <Stack sx={{ px: '13px', py: '8px' }}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ px: 1.5, pt: 1, }}>

                    <Stack display={"flex"} flexDirection={"row"}>
                      <Box sx={{
                        borderRadius: '16px',
                        background: '#18A155',
                        width: '6px',
                        height: '20px',
                        mr: "8px"
                      }} />
                      <Typography fontSize={'14px'} color={'rgba(99, 115, 129, 1)'}>{t('dashboard.global.brandPulse.organic')}</Typography>
                    </Stack>

                    <Typography fontSize={'14px'} color={'rgba(51, 60, 71, 1)'}>{formatNumber(params?.row?.organic_volume_compare?.toFixed(0))}</Typography>

                  </Stack>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ px: 1.5, pt: 1 }}>
                    <Stack display={"flex"} flexDirection={"row"}>
                      <Box sx={{
                        borderRadius: '16px',
                        background: '#DAA335',
                        width: '6px',
                        height: '20px',
                        mr: "8px"
                      }} />
                      <Typography fontSize={'14px'} color={'rgba(99, 115, 129, 1)'}>{t('dashboard.global.brandPulse.paid')}</Typography>
                    </Stack>
                    <Typography fontSize={'14px'} color={'rgba(51, 60, 71, 1)'}>{formatNumber(params?.row?.paid_volume_compare?.toFixed(0))}</Typography>
                  </Stack>
                </Stack>
              </Stack>
            </Box>
          );

          return (
            <>
              {params.row.data_status &&
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ width: '100%', height: "100%" }}
                >
                  <Typography color={"rgba(47, 56, 67, 1)"} variant="caption" sx={{ minWidth: 30, textAlign: 'right' }} fontSize={'0.875rem'}>
                    {params?.row?.organic_compare?.toFixed(0)}%
                  </Typography>

                  <CustomTooltip noPadding title={tooltipContent} placement="top">

                    <Box
                      sx={{
                        position: 'relative',
                        flexGrow: 1,
                        height: 8,
                        border: "none",
                        borderRadius: 1,
                        backgroundColor: 'white',
                        display: 'flex',
                      }}
                    >
                      <Box
                        sx={{
                          width: `${params?.row?.organic_compare?.toFixed(0)}%`,
                          backgroundColor: '#18A155',
                          borderRadius: "2px",
                        }}
                      />

                      <Box
                        sx={{
                          width: `${params?.row?.paid_compare?.toFixed(0)}%`,
                          backgroundColor: '#DAA335',
                          borderRadius: "2px",
                          ml: '2px',
                        }}
                      />
                    </Box>
                  </CustomTooltip>

                  <Typography color={"rgba(47, 56, 67, 1)"} variant="caption" sx={{ minWidth: 30, textAlign: 'left' }} fontSize={'0.875rem'}>
                    {params?.row?.paid_compare?.toFixed(0)}%
                  </Typography>
                </Stack>
              }
            </>
          );
        }
      } : null,
    {
      field: 'monthly_spend',
      headerAlign: "right",
      flex: 1,
      headerName: t('dashboard.global.brandPulse.monthly_spend'),
      renderHeader: () => (
        <CustomTooltip
          title={t('dashboard.global.brandPulse.definitions.monthly_spend')}
          placement="top"
        >
          <Box
            sx={{
              cursor: "help",
            }}
          >
            <Typography
              sx={{
                whiteSpace: "normal",
                wordBreak: "break-word",
                lineHeight: 1.2,
              }}
              color="#637381"
              fontSize="14px"
              fontWeight={600}
            >
              {t('dashboard.global.brandPulse.monthly_spend')}
            </Typography>
          </Box>
        </CustomTooltip>
      ),
      type: "number",
      renderCell: (params: any) => (
        <>
          {params.row.data_status &&
            <Stack spacing={1} direction="row" alignItems="center" justifyContent={"flex-end"} sx={{ px: 1, width: 1, height: 1 }}>
              <Typography fontSize={"14px"}>
                {formatCurrency(params.row.monthly_spend, workspace?.currency, lang, true, 1)}
              </Typography>
            </Stack>
          }
        </>
      ),
    },
    selectedComparisonPeriod?.value !== 'no_comparison' ?
      {
        field: 'monthly_spend_compare',
        headerAlign: "right",
        flex: 1,
        headerName: t('dashboard.global.brandPulse.monthly_spend_compare'),
        renderHeader: () => (
          <CustomTooltip
            title={t('dashboard.global.brandPulse.definitions.monthly_spend')}
            placement="top"
          >
            <Box
              sx={{
                cursor: "help",
              }}
            >
              <Typography
                sx={{
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                  lineHeight: 1.2,
                }}
                color="#637381"
                fontSize="14px"
                fontWeight={600}
              >
                {t('dashboard.global.brandPulse.monthly_spend_compare')}
              </Typography>
            </Box>
          </CustomTooltip>
        ),
        type: "number",
        renderCell: (params: any) => (
          <>
            {params.row.data_status &&
              <Stack spacing={1} direction="row" alignItems="center" justifyContent={"flex-end"} sx={{ px: 1, width: 1, height: 1 }}>
                <Typography fontSize={"14px"}>
                  {formatCurrency(params.row.monthly_spend_compare, workspace?.currency, lang, true, 1)}
                </Typography>
              </Stack>
            }
          </>
        ),
      } : null,
    selectedComparisonPeriod?.value !== 'no_comparison' ?
      {
        field: 'monthly_spend_difference',
        flex: 1,
        headerName: t('dashboard.global.brandPulse.monthly_spend_difference'),
        renderHeader: () => (
          <CustomTooltip
            title={t('dashboard.global.brandPulse.definitions.monthly_spend')}
            placement="top"
          >
            <Box
              sx={{
                cursor: "help",
              }}
            >
              <Typography
                sx={{
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                  lineHeight: 1.2,
                }}
                color="#637381"
                fontSize="14px"
                fontWeight={600}
              >
                {t('dashboard.global.brandPulse.monthly_spend_difference')}
              </Typography>
            </Box>
          </CustomTooltip>
        ),
        type: "number",
        headerAlign: "left",
        renderCell: (params: any) => {
          const current = params.row.monthly_spend || 0;
          const compare = params.row?.monthly_spend_compare || 0;

          const diffValue = current - compare;
          const diffPercent = compare !== 0 ? (diffValue / compare) * 100 : 0;

          return (
            <>
              {
                params.row.data_status &&

                <DatagridDiffCell
                  value={formatNumber(diffValue)}
                  compareChip={diffPercent}
                  colors={{
                    dark: '#db8377',
                    light: '#B71D18'
                  }}
                />
              }
            </>
          );
        }
      } : null,
    {
      field: 'visibility_score',
      flex: 1,
      type: "number",
      headerAlign: "right",
      headerName: t('dashboard.global.brandPulse.visibility_rate'),
      renderHeader: () => (
        <CustomTooltip
          title={t('dashboard.global.brandPulse.definitions.visibility_rate')}
          placement="top"
        >
          <Box
            sx={{
              cursor: "help",
            }}
          >
            <Typography
              sx={{
                whiteSpace: "normal",
                wordBreak: "break-word",
                lineHeight: 1.2,
              }}
              color="#637381"
              fontSize="14px"
              fontWeight={600}
            >
              {t('dashboard.global.brandPulse.visibility_rate')}
            </Typography>
          </Box>
        </CustomTooltip>
      ),
      renderCell: (params: GridRenderCellParams) => {

        const data = {
          organicOnly: Number(params?.row?.organic_presence?.toFixed(0)) || 0,
          soloBidder: Number(params?.row?.solo_bidder?.toFixed(0)) || 0,
          topAdPosition: Number(params?.row?.top_ad_position?.toFixed(0)) || 0,
          competitorOnly: Number(params?.row?.compet_only?.toFixed(0)) || 0,
        };

        return (
          <>
            {params.row.data_status &&
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ width: '100%', height: "100%" }}
              >
                <Typography variant="body2">{fPercent(params?.row?.visibility_score)}</Typography>
                <Stack direction="row" alignItems="center" spacing={1} py={1} sx={{ width: "100%", height: "52px" }}>
                  <PresenceRateChart data={data} height={40} totalVisibilityRate={params?.row?.visibility_score} />
                </Stack>
              </Stack>
            }
          </>
        )
      },
    },
    selectedComparisonPeriod?.value !== 'no_comparison' ?
      {
        field: 'visibility_rate_compare',
        flex: 1,
        type: "number",
        headerName: t('dashboard.global.brandPulse.visibility_rate_compare'),
        headerAlign: "right",
        renderHeader: () => (
          <CustomTooltip
            title={t('dashboard.global.brandPulse.definitions.visibility_rate')}
            placement="top"
          >
            <Box
              sx={{
                cursor: "help",
              }}
            >
              <Typography
                sx={{
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                  lineHeight: 1.2,
                }}
                color="#637381"
                fontSize="14px"
                fontWeight={600}
              >
                {t('dashboard.global.brandPulse.visibility_rate_compare')}
              </Typography>
            </Box>
          </CustomTooltip>
        ),
        renderCell: (params: GridRenderCellParams) => {

          const data = {
            organicOnly: Number(params?.row?.organic_presence_compare?.toFixed(0)) || 0,
            soloBidder: Number(params?.row?.solo_bidder_compare?.toFixed(0)) || 0,
            topAdPosition: Number(params?.row?.top_ad_position_compare?.toFixed(0)) || 0,
            competitorOnly: Number(params?.row?.compet_only_compare?.toFixed(0)) || 0,
          };

          return (
            <>
              {params.row.data_status &&
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ width: '100%', height: "100%" }}
                >
                  <Typography variant="body2">{fPercent(params?.row?.visibility_score_compare?.toFixed(0))}</Typography>
                  <Stack direction="row" alignItems="center" spacing={1} py={1} sx={{ width: "100%", height: "52px" }}>
                    <PresenceRateChart data={data} height={40} totalVisibilityRate={params?.row?.visibility_score_compare} />
                  </Stack>
                </Stack>
              }
            </>
          )
        },
      } : null,
    selectedComparisonPeriod?.value !== 'no_comparison'
      ? {
        field: 'visibility_score_diff',
        flex: 1,
        headerAlign: "right",
        renderHeader: () => (
          <CustomTooltip
            title={t('dashboard.global.brandPulse.definitions.visibility_rate')}
            placement="top"
          >
            <Box
              sx={{
                cursor: "help",
              }}
            >
              <Typography
                sx={{
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                  lineHeight: 1.2,
                }}
                color="#637381"
                fontSize="14px"
                fontWeight={600}
              >
                {t('dashboard.global.brandPulse.visibility_rate_difference')}
              </Typography>
            </Box>
          </CustomTooltip>
        ),
        type: "number",
        headerName: t('dashboard.global.brandPulse.visibility_rate_difference'),
        renderCell: (params: GridRenderCellParams) => {

          const total = ['organic_presence', 'solo_bidder', 'top_ad_position', 'compet_only']
            .reduce((sum, key) => sum + (Number(params.row[key]) || 0), 0);

          const total_compare = ['organic_presence_compare', 'solo_bidder_compare', 'top_ad_position_compare', 'compet_only_compare']
            .reduce((sum, key) => sum + (Number(params.row[key]) || 0), 0);

          const diffPercent = total ? ((total_compare - total) / total) * 100 : 0;

          return (
            <>
              {params.row.data_status &&
                <CompareChip value={diffPercent} invertedColor={false} />
              }
            </>
          )
        },
      }
      : null,
  ].filter(Boolean) as GridColDef[];


  const CustomNoRowsOverlay = () => {
    return (
      <Stack
        height="100%"
        alignItems="center"
        justifyContent="center"
        sx={{
          backgroundColor: 'white',
          color: '#637381',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
          }}
        >
          <Box
            sx={{
              backgroundColor: 'rgba(229, 218, 253, 0.40)',
              p: '12px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Iconify color={"rgba(118, 85, 193, 1)"} icon="clarity:paste-line" width={48} height={48} />
          </Box>
        </Box>

        <Typography fontSize={'16px'} color={"rgba(51, 60, 71, 1)"} variant="subtitle1" fontWeight={600} mt={"12px"}>
          No selected brands
        </Typography>
        <Box mt={"12px"} display={"flex"} flexDirection={"row"}>
          <Typography color={"rgba(99, 115, 129, 1)"} fontSize={'14px'} textAlign={"center"} variant="body2">
            You haven’t Selected any competitors currently, go to <br />
            <Box
              component="span"
              sx={{
                color: "rgba(81, 25, 183, 1)",
                cursor: "pointer",
                fontSize: "16px",
                textDecoration: "underline",
                marginRight: .6, // MUI spacing scale (4px * 1)
                "&:hover": {
                  color: "rgba(81, 25, 183, 0.75)"
                }
              }}
              onClick={() => navigate("/brand-pulse/competitors-selection")}
            >
              Competitors Selection
            </Box>
            tab to select few brands and track them.
          </Typography>
        </Box>
      </Stack>
    );
  };

  const { data: brandsData, isLoading: isBrandsLoading, isFetching } = useGetBrandsQuery(
    {
      workspace_id: workspaceId,
      country: filterStore?.countries[0],
      date: selectedDateRange?.value,
      compareDate: selectedComparisonPeriod?.value,
      industries: selectedIndustries
    },
    {
      skip: !workspaceId,
      refetchOnMountOrArgChange: true,
    }
  );

  const clientDomain = workspace?.url || "";
  const friendlyDomain = workspace?.friendlyDomains || [];

  const { rows } = useMemo(() => {
    if (!brandsData?.data) return { rows: [] };

    const dataCopy = [...brandsData.data];

    const readyRows = dataCopy
      .filter(r => r.data_status === true)
      .sort((a, b) => (b.perc_clicks || 0) - (a.perc_clicks || 0));


    const finalRows = [
      ...readyRows
    ].map((brand, index) => ({
      id: index + 1,
      rank: brand?.rank,
      domains: brand?.domain_name,
      industry: brand?.industry,


      organic_presence: brand?.organic_presence,
      solo_bidder: brand?.solo_bidder,
      top_ad_position: brand?.top_ad_position,
      compet_only: brand?.compet_only,

      organic_presence_compare: brand?.compare?.organic_presence,
      solo_bidder_compare: brand?.compare?.solo_bidder,
      top_ad_position_compare: brand?.compare?.top_ad_position,
      compet_only_compare: brand?.compare?.compet_only,


      data_status: brand?.data_status,

      organic_volume: brand?.organic_volume,
      paid_volume: brand?.paid_volume,
      organic: brand?.organic,
      paid: brand?.paid,

      organic_volume_compare: brand?.compare?.organic_volume,
      paid_volume_compare: brand?.compare?.paid_volume,
      organic_compare: brand?.compare?.organic,
      paid_compare: brand?.compare?.paid,

      monthly_searches: brand?.monthly_searches,
      monthly_search_volume_compare: brand?.compare?.monthly_searches || 0,

      monthly_spend: brand?.monthly_spend,
      monthly_spend_compare: brand?.compare?.monthly_spend,

      visibility_score: brand?.visibility_score,
      visibility_score_compare: brand?.compare?.visibility_score,

      perc_clicks: brand?.perc_clicks || 0,
    }));


    return {
      rows: finalRows,
    };
  }, [brandsData, workspace?.url, workspace?.friendlyDomains, filterStore?.countries[0]]);

  const allRows = [...rows];
  const hasRows = allRows.length > 0;

  const countriesData = [
    {
      "country_name": "France",
    },
    {
      "country_name": "United States",
    }
  ]

  const isLoading = isBrandsLoading || isFetching || !brandsData;

  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({
    organic_vs_paid_compare: false,
    visibility_rate_compare: false,
    monthly_search_volume_compare: false,
    visibility_score_diff: false,
    monthly_spend_compare: false,
  });

  const handleChangeColumnVisibilityModel = useCallback((newModel: GridColumnVisibilityModel) => {
    setColumnVisibilityModel(newModel);
  }, []);


  return (
    <>
      <Helmet>
        <title>Leaderboard</title>
      </Helmet>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 3, mt: 9 }}>

        <RegionSelector data={countriesData} displayKPI={false} />


        <Box px={'24px'} sx={{ marginTop: "10px", height: 700 }}>
          {isLoading
            ? <Skeleton width={'100%'} height={700} variant="rectangular" animation="wave" sx={{
              borderRadius: '8px',
            }} />
            : <DataGridPro
              sx={{
                border: "1px solid rgba(145, 158, 171, 0.2)",
                borderRadius: '8px',
                '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-cell:focus': {
                  outline: 'none !important',
                },
                '& .MuiDataGrid-columnHeader, .MuiDataGrid-filler': {
                  backgroundColor: 'rgba(226, 229, 233, 0.16) !important',
                  color: "rgba(99, 115, 129, 1)",
                  fontWeight: 'normal!important'
                },
                '& .MuiDataGrid-cell': {
                  border: "1px solid rgba(145, 158, 171, 0.2)",
                  color: 'rgba(51, 60, 71, 1)',
                  backgroundColor: 'white',
                },
                "& .client-row .MuiDataGrid-cell": {
                  backgroundColor: "rgba(0, 143, 205, 0.06)",
                  fontWeight: "bold",
                },
                "& .data-not-ready .MuiDataGrid-cell": {
                  backgroundColor: "rgba(118, 53, 220, 0.08)",
                  fontWeight: "bold",
                },
                width: '100%',
                '& .highlighted-row': {
                  opacity: 0.3,
                },
              }}
              getRowClassName={(params) => {
                const classes = [];
                if (params.row.domains === clientDomain) classes.push("client-row");
                if (friendlyDomain?.includes(params.row.domains)) classes.push("client-row");

                if (params.row.data_status === false) classes.push("data-not-ready");
                return classes.join(" ");
              }}
              apiRef={apiRef}
              pageSizeOptions={[10, 25, 50]}
              columnVisibilityModel={columnVisibilityModel}
              onColumnVisibilityModelChange={handleChangeColumnVisibilityModel}
              columns={columns}
              pagination={hasRows}
              rows={allRows}
              density="standard"
              disableRowSelectionOnClick
              paginationModel={paginationModel}
              rowHeight={53}
              onPaginationModelChange={setPaginationModel}
              slots={{
                toolbar: CustomToolbar,
                footer: () => (
                  <CustomFooter
                    rows={allRows}
                    paginationModel={paginationModel}
                    onPageSizeChange={(newSize: number) =>
                      setPaginationModel((prev) => ({
                        ...prev,
                        pageSize: newSize,
                        page: 0,
                      }))
                    }
                  />
                )
              }}

            />
          }
        </Box>
      </Box>

      <ModalTrackingBrand
        page={"Leaderboard"}
        region={filterStore?.countries[0]}
        open={modalTrackingBrand?.open}
        domain={modalTrackingBrand?.domain} onClose={() => setModalTrackingBrand({ open: false, domain: null })}
      />

    </>
  )
}